import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * In-page confirmation for destructive actions, instead of the browser's
 * window.confirm. Focus starts on Cancel so a stray Enter can't delete anything.
 */
export default function ConfirmDialog({ title, children, confirmLabel = 'Delete', busyLabel = 'Working…', danger = true, icon = '⚠', busy, onConfirm, onCancel }) {
  const cancelRef = useRef(null);

  useEffect(() => { cancelRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onCancel, busy]);

  return (
    <div className="confirm-backdrop" onClick={() => { if (!busy) onCancel(); }}>
      <div className="confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title"
        onClick={e => e.stopPropagation()}>
        <div className={`confirm-icon ${danger ? '' : 'calm'}`} aria-hidden="true">{icon}</div>
        <h2 id="confirm-title">{title}</h2>
        <div className="confirm-body">{children}</div>
        <div className="btn-row">
          <button type="button" className="btn" ref={cancelRef} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className={`btn ${danger ? 'dng' : 'pri'}`} onClick={onConfirm} disabled={busy}>
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Promise-style wrapper so call sites read like window.confirm:
 *
 *   const [ask, confirmDialog] = useConfirm();
 *   if (!await ask({ title: 'Remove the logo?', confirmLabel: 'Remove' })) return;
 *   ...
 *   return <>{...}{confirmDialog}</>;
 *
 * `body` is the optional explanatory text; `danger: false` gives a calm,
 * non-red confirm button for actions that are consequential but not destructive.
 */
export function useConfirm() {
  const [state, setState] = useState(null);
  const ask = useCallback((opts) => new Promise(resolve => setState({ ...opts, resolve })), []);
  const settle = (value) => { state.resolve(value); setState(null); };
  const dialog = state ? (
    <ConfirmDialog title={state.title} confirmLabel={state.confirmLabel} danger={state.danger !== false}
      icon={state.icon} onConfirm={() => settle(true)} onCancel={() => settle(false)}>
      {state.body ? <p style={{ margin: 0 }}>{state.body}</p> : null}
    </ConfirmDialog>
  ) : null;
  return [ask, dialog];
}
